import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, ChevronDown } from "lucide-react";
import { IMAGES, DIVISIONI, PROFILO_GRUPPO } from "@/data/content";
import { Reveal, GoldLine, SectionHeading } from "@/components/Reveal";
import { useLang } from "@/i18n";

const HeroWord = ({ children, delay, gold = false }) => (
  <span className="inline-block overflow-hidden pb-1">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
      className={`inline-block ${gold ? "gold-gradient-text italic" : "text-[#F5F2EB]"}`}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { t } = useLang();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-screen min-h-[640px] overflow-hidden">
      <motion.div style={{ y: imgY }} className="absolute inset-0">
        <img
          src={IMAGES.heroHome}
          alt=""
          className="w-full h-full object-cover scale-105 brightness-[0.5]"
        />
      </motion.div>
      <div className="absolute inset-0 bg-[#080808]/60" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#080808]/60 via-transparent to-[#080808]" />

      <div className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-center items-center text-center">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="eyebrow mb-8"
        >
          {t("hero.eyebrow")}
        </motion.p>
        <h1 className="font-display font-light tracking-tight text-5xl sm:text-6xl lg:text-8xl leading-[1.05]">
          <HeroWord delay={0.35}>ITALIAN</HeroWord>{" "}
          <HeroWord delay={0.5}>EXCELLENCE.</HeroWord>
          <br />
          <HeroWord delay={0.75} gold>GLOBAL</HeroWord>{" "}
          <HeroWord delay={0.9} gold>VISION.</HeroWord>
        </h1>
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 1.4, delay: 1.3, ease: [0.22, 1, 0.36, 1] }}
          className="gold-line origin-center w-40 sm:w-56 mt-10"
        />
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.5 }}
          className="mt-8 max-w-xl text-base sm:text-lg text-[#A5A5A5] font-light leading-relaxed"
        >
          {t("hero.sub")}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.7 }}
          className="mt-12 flex flex-wrap items-center justify-center gap-5"
        >
          <Link
            to="/diventa-partner"
            data-testid="hero-cta-partnership"
            className="group px-8 py-4 bg-[#C6A45D] text-[#080808] text-xs tracking-[0.25em] uppercase font-semibold hover:bg-[#E5C776] transition-colors duration-300 flex items-center gap-3"
          >
            {t("hero.ctaPartner")}
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <a
            href="#divisioni"
            data-testid="hero-cta-divisioni"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById("divisioni")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="px-8 py-4 border border-[#C6A45D]/60 text-[#C6A45D] text-xs tracking-[0.25em] uppercase hover:border-[#C6A45D] hover:bg-[#C6A45D]/10 transition-colors duration-300"
          >
            {t("hero.ctaUnits")}
          </a>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
        data-testid="scroll-indicator"
      >
        <span className="text-[10px] tracking-[0.4em] uppercase text-[#A5A5A5]">{t("hero.scroll")}</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
          <ChevronDown size={18} className="text-[#C6A45D]" />
        </motion.div>
      </motion.div>
    </section>
  );
};

const Intro = () => {
  const { t } = useLang();
  return (
  <section data-testid="intro-section" className="bg-[#080808] py-24 lg:py-32">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5">
        <SectionHeading eyebrow={t("home.introEyebrow")} title={t("home.introTitle")} goldWord={t("home.introGold")} />
      </div>
      <div className="lg:col-span-7 flex flex-col justify-end">
        <Reveal delay={0.2}>
          <p className="text-lg sm:text-xl font-light text-[#A5A5A5] leading-relaxed">
            {PROFILO_GRUPPO.testo}
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <Link
            to="/chi-siamo"
            data-testid="intro-link-chi-siamo"
            className="group mt-10 inline-flex items-center gap-3 text-xs tracking-[0.3em] uppercase text-[#C6A45D] hover:text-[#E5C776] transition-colors duration-300"
          >
            {t("home.introLink")}
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
  );
};

const CardCta = () => {
  const { t } = useLang();
  return <>{t("home.discover")}</>;
};

const DivisionCard = ({ d, index }) => (
  <Reveal delay={(index % 3) * 0.12}>
    <Link
      to={`/business-unit/${d.id}`}
      data-testid={`division-card-${d.id}`}
      className="group relative block bg-[#141414] border border-[#1D1D1D] hover:border-[#C6A45D]/70 transition-colors duration-500 overflow-hidden"
    >
      <div className="relative h-64 sm:h-72 overflow-hidden">
        <img
          src={d.img}
          alt={`Billions ${d.nome}`}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#141414] via-[#080808]/30 to-transparent" />
      </div>
      <div className="p-8">
        <p className="eyebrow mb-3">Billions · {d.numero}</p>
        <h3 className="font-display text-2xl sm:text-3xl font-light text-[#F5F2EB] group-hover:text-[#E5C776] transition-colors duration-300">
          {d.nome}
        </h3>
        <p className="mt-2 text-sm text-[#C6A45D] italic font-display">{d.payoff}</p>
        <p className="mt-4 text-sm text-[#A5A5A5] leading-relaxed font-light">{d.descrizioneCard}</p>
        <p className="mt-5 inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#C6A45D]/0 group-hover:text-[#C6A45D] transition-colors duration-500">
          <CardCta /> <ArrowRight size={12} />
        </p>
      </div>
      <div className="absolute top-0 left-0 h-px w-0 bg-gradient-to-r from-[#8F6B32] via-[#C6A45D] to-[#E5C776] transition-[width] duration-700 group-hover:w-full" />
    </Link>
  </Reveal>
);

const Divisioni = () => {
  const { t } = useLang();
  return (
  <section id="divisioni" data-testid="divisions-section" className="bg-[#141414] py-24 lg:py-32 scroll-mt-20">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <SectionHeading eyebrow={t("home.unitsEyebrow")} title={t("home.unitsTitle")} goldWord={t("home.unitsGold")} />
      <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {DIVISIONI.map((d, i) => (
          <DivisionCard key={d.id} d={d} index={i} />
        ))}
      </div>
    </div>
  </section>
  );
};

const Valori = () => {
  const { t } = useLang();
  const items = [
    { n: "01", t: t("home.v1t"), d: t("home.v1d") },
    { n: "02", t: t("home.v2t"), d: t("home.v2d") },
    { n: "03", t: t("home.v3t"), d: t("home.v3d") },
  ];
  return (
    <section data-testid="values-section" className="bg-[#080808] py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <SectionHeading eyebrow={t("home.valuesEyebrow")} title={t("home.valuesTitle")} goldWord={t("home.valuesGold")} />
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#C6A45D]/15">
          {items.map((v, i) => (
            <Reveal key={v.n} delay={i * 0.15}>
              <div data-testid={`value-card-${i}`} className="bg-[#080808] p-10 h-full group hover:bg-[#141414] transition-colors duration-500">
                <span className="font-display text-5xl font-light gold-gradient-text">{v.n}</span>
                <h3 className="mt-6 font-display text-2xl font-light text-[#F5F2EB]">{v.t}</h3>
                <p className="mt-4 text-sm text-[#A5A5A5] leading-relaxed font-light">{v.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const CtaFinale = () => {
  const { t } = useLang();
  return (
  <section data-testid="cta-section" className="relative bg-[#141414] py-28 lg:py-36 overflow-hidden">
    <div className="absolute inset-0 opacity-[0.07]">
      <img src={IMAGES.chiSiamoHero} alt="" className="w-full h-full object-cover" />
    </div>
    <div className="relative max-w-4xl mx-auto px-6 text-center">
      <Reveal>
        <p className="eyebrow mb-6">{t("home.ctaEyebrow")}</p>
      </Reveal>
      <Reveal delay={0.1}>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light text-[#F5F2EB] tracking-tight">
          {t("home.ctaTitle")} <span className="gold-gradient-text italic">{t("home.ctaGold")}</span>
        </h2>
      </Reveal>
      <GoldLine className="mt-10 w-32 mx-auto" />
      <Reveal delay={0.2}>
        <p className="mt-8 text-[#A5A5A5] font-light leading-relaxed max-w-xl mx-auto">
          {t("home.ctaText")}
        </p>
      </Reveal>
      <Reveal delay={0.3}>
        <Link
          to="/diventa-partner"
          data-testid="cta-partnership-button"
          className="group mt-12 inline-flex items-center gap-3 px-10 py-4 bg-[#C6A45D] text-[#080808] text-xs tracking-[0.25em] uppercase font-semibold hover:bg-[#E5C776] transition-colors duration-300"
        >
          {t("home.ctaButton")}
          <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  </section>
  );
};

export default function Home() {
  return (
    <main data-testid="home-page">
      <Hero />
      <Intro />
      <Divisioni />
      <Valori />
      <CtaFinale />
    </main>
  );
}
